import { lensPath, set } from "ramda";
import { odometer, usesRearShoesAndDrums, updloadVin, FieldObject } from "./fields";
import {
  brakeFluid,
  frontPads,
  frontRotors,
  inspectedServices,
  inspectionPrivateNotes,
  rearDrums,
  rearPads,
  rearRotors,
  rearShoes,
  licensePlate,
  SectionObject,
  frontPadsCore,
  frontRotorsCore,
  brakeFluidCore,
  rearPadsCore,
  inspectedServicesCore,
  rearRotorsCore,
  batteryHealthSection,
  frontPadsFleet,
  rearPadsFleet,
  tireTreadDepth,
} from "./sections";
import { File, Job, VehicleType } from "../../../generated/nest-graphql";
import { VIN_FILE_CATEGORY } from "../../PreJobChecklistV2/constants";
import { fleetJobTypes } from "../../../lib/constants";

const parseForm = (item: FormItemsRecord): FormItems =>
  Object.entries(item).map(([key, value]) => {
    if ("children" in value && typeof value.children === "object") {
      return {
        ...value,
        children: parseForm(value.children as FormItemsRecord),
      };
    } else {
      return value;
    }
  });

type InjectProperty = {
  route: string;
  value: any;
};

export type FormItems = (SectionObject | FieldObject)[];
type FormItemsRecord = Record<string, SectionObject | FieldObject>;

type ViocProps = {
  hasVinInfo?: boolean;
  hasLp?: boolean;
  hasCCA?: boolean;
};

type CoreProps = {
  hasVinInfo?: boolean;
  hasLp?: boolean;
  hasCCA?: boolean;
};

type formFunction<T> = (value: Record<string, any>, injectProperties?: InjectProperty[], otherProps?: T) => FormItems;

export const ViocPreJobCheckList: formFunction<ViocProps> = (
  values,
  injectProperties,
  { hasVinInfo, hasLp, hasCCA }
) => {
  let dependsCheckbox = values.usesRearShoesAndDrums
    ? {
        rearShoes: rearShoes(),
        rearDrums: rearDrums(),
      }
    : {
        rearPads: rearPads(),
        rearRotors: rearRotors(),
      };

  let result = {
    odometer: odometer({ required: true, useAllRow: true, className: "mb-3" }),
    brakeFluid: brakeFluid(),
    frontPads: frontPads(),
    frontRotors: frontRotors(),
    usesRearShoesAndDrums: usesRearShoesAndDrums({ useAllRow: true, className: "mb-3" }),
    ...dependsCheckbox,
    // ...(hasCCA ? { batteryHealth: batteryHealthSection() } : null),
    batteryHealth: batteryHealthSection(),
    inspectedServices: inspectedServices(),
    inspectionPrivateNotes: inspectionPrivateNotes(),
    // ...(!hasVinInfo ? { uploadVin: updloadVin() } : null),
    // ...(hasVinInfo || hasLp ? { licensePlate: licensePlate() } : null),
  };
  if (injectProperties?.length) {
    injectProperties.forEach(({ route, value }) => {
      result = set(lensPath(route.split(".")), value, result);
    });
  }
  return parseForm(result);
};

export const CorePreJobCheckList: formFunction<CoreProps> = (
  values,
  injectProperties,
  { hasLp, hasVinInfo, hasCCA }
) => {
  let dependsCheckbox = values.usesRearShoesAndDrums
    ? {
        rearShoes: rearShoes(),
        rearDrums: rearDrums(),
      }
    : {
        rearPads: rearPadsCore(),
        rearRotors: rearRotorsCore(),
      };

  let result = {
    odometer: odometer({ required: true, useAllRow: true, className: "mb-3" }),
    brakeFluid: brakeFluidCore(),
    frontPads: frontPadsCore(),
    frontRotors: frontRotorsCore(),
    usesRearShoesAndDrums: usesRearShoesAndDrums({ useAllRow: true, className: "mb-3" }),
    ...dependsCheckbox,
    // ...(hasCCA ? { batteryHealth: batteryHealthSection() } : null),
    batteryHealth: batteryHealthSection(),
    inspectedServices: inspectedServicesCore(),
    inspectionPrivateNotes: inspectionPrivateNotes(),
    ...(hasVinInfo || hasLp ? { licensePlate: licensePlate({ required: false }) } : null),
  };
  if (injectProperties?.length) {
    injectProperties.forEach(({ route, value }) => {
      result = set(lensPath(route.split(".")), value, result);
    });
  }
  return parseForm(result);
};

export const FleetPreJobCheckList: formFunction<CoreProps> = (
  values,
  injectProperties,
  { hasLp, hasVinInfo, hasCCA }
) => {
  let dependsCheckbox = values.usesRearShoesAndDrums
    ? {
        rearShoes: rearShoes({ usePhotoMode: true }),
        rearDrums: rearDrums({ usePhotoMode: true }),
      }
    : {
        rearPads: rearPadsFleet({ usePhotoMode: true }),
        rearRotors: rearRotorsCore({ usePhotoMode: true }),
      };

  let result = {
    odometer: odometer({ required: true, useAllRow: true, className: "mb-3", usePhotoMode: true }),
    brakeFluid: brakeFluidCore({ usePhotoMode: true }),
    frontPads: frontPadsFleet({ usePhotoMode: true }),
    frontRotors: frontRotorsCore({ usePhotoMode: true }),
    usesRearShoesAndDrums: usesRearShoesAndDrums({ useAllRow: true, className: "mb-3", usePhotoMode: true }),
    ...dependsCheckbox,
    // ...(hasCCA ? { batteryHealth: batteryHealthSection() } : null),
    batteryHealth: batteryHealthSection({ usePhotoMode: true }),
    inspectedServices: inspectedServicesCore({ usePhotoMode: true }),
    inspectionPrivateNotes: inspectionPrivateNotes({ usePhotoMode: true }),
    tireTreadDepth: tireTreadDepth({ usePhotoMode: true }),
    ...(hasVinInfo || hasLp ? { licensePlate: licensePlate({ required: false, usePhotoMode: true }) } : null),
  };
  if (injectProperties?.length) {
    injectProperties.forEach(({ route, value }) => {
      result = set(lensPath(route.split(".")), value, result);
    });
  }
  return parseForm(result);
};

interface getFormProps {
  job?: Job;
  vehicle?: VehicleType;
  files?: File[];
  values?: any;
}

const toOptionalFixed = (value: number, digits: number) => `${Number.parseFloat(value.toFixed(digits))}`;

export const getForm = ({ job, values, vehicle, files }: getFormProps) => {
  const isVioc = job.type === "Vioc Inspection";
  const isFleet = fleetJobTypes.includes(job.type);
  const isInternalMaintenance = job.type === "Internal Van Maintenance";
  const hasVinFile = files?.some((file) => file?.category === VIN_FILE_CATEGORY && file?.jobId === job.id);
  let frontRotorsDiscardThicknessInches;

  if (job?.vehicleInfo?.frontRotorsDiscardThicknessInches) {
    frontRotorsDiscardThicknessInches = job.vehicleInfo.frontRotorsDiscardThicknessInches;
  } else {
    frontRotorsDiscardThicknessInches = vehicle?.vehicleTechnicalInfo?.frontRotorsDiscardThicknessInches;
  }
  const rearRotorsDiscardThicknessInches = vehicle?.vehicleTechnicalInfo.rearRotorsDiscardThicknessInches;
  const baseInjectProperties = [
    {
      route: "frontRotors.children.thicknessInches.endAdornment.secondColorGradeParam",
      value: frontRotorsDiscardThicknessInches?.toString(),
    },
    {
      route: "rearRotors.children.thicknessInches.endAdornment.secondColorGradeParam",
      value: (rearRotorsDiscardThicknessInches ?? frontRotorsDiscardThicknessInches)?.toString(),
    },
    // {
    //   route: "batteryHealth.children.coldCrankingAmps.disabled",
    //   value: values.withoutCCA,
    // },
    // {
    //   route: "batteryHealth.children.coldCrankingAmps.endAdornment.secondColorGradeParam",
    //   value: vehicle.CCA,
    // },
    {
      route: "batteryHealth.children.batteryHealth.disabled",
      value: values.withoutBatteryHealth,
    },
  ];
  const hasVinInfo = !!job.vehicleInfo?.vin;

  const hasCCA = !!(vehicle?.CCA ?? job.vehicleInfo?.CCA);

  const hasLp = !!job.vehicleInfo?.licensePlate;
  const otherProps = { hasVinInfo, hasLp, hasCCA };
  if (isVioc) {
    return ViocPreJobCheckList(
      values,
      [
        ...baseInjectProperties,
        {
          route: "frontRotors.description",
          value: `Dicard thickness: ${
            frontRotorsDiscardThicknessInches
              ? `${toOptionalFixed(frontRotorsDiscardThicknessInches, 8)} inches`
              : "Not provided"
          }`,
        },
        {
          route: "uploadVin.hasVinFile",
          value: hasVinFile,
        },
        {
          route: "uploadVin.contactId",
          value: job?.contact?.id,
        },
        {
          route: "uploadVin.jobId",
          value: job?.id,
        },
      ],
      otherProps
    );
  }
  if (isFleet || isInternalMaintenance) {
    return FleetPreJobCheckList(
      values,
      [
        ...baseInjectProperties,
        {
          route: "tireTreadDepth.children.rearDepth.disabled",
          value: values.tireTreadDepth?.withoutTreadDepth,
        },
        {
          route: "tireTreadDepth.children.frontDepth.disabled",
          value: values.tireTreadDepth?.withoutTreadDepth,
        },
      ],
      otherProps
    );
  }

  return CorePreJobCheckList(values, baseInjectProperties, otherProps);
};
